'use client';
import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grow,
  Fade,
  Typography,
  CircularProgress,
} from '@mui/material';

import ExpandMore from '@mui/icons-material/ExpandMore';
import OptionSelectedFeedback from './elements/option-selected-feedback';
import Stepper from './elements/stepper';
import Heading from './elements/heading';
import { CreativeRadio, LoadingSpinner, RadioOptionGroup } from '@/shared/ui';
import { EventBus } from '@/shared/utils';
import { OnboardingEventKeys, OnboardingEvents } from '../events';
import { useOnboarding } from '../hooks/use-onboarding';
import { useOnboardingStore } from '../stores/onboarding-store';

interface MusicJourneySelectionProps {
  //    Definisci qui i tuoi tipi di prop, ad esempio:
  eventBus: EventBus<OnboardingEvents>;
}

const levelOptions = [
  {
    name: 'Sono agli inizi',
    imageSrc: '/assets/images/onboarding/percorso-no-strumento.svg',
    value: 'Sono agli inizi',
  },
  {
    name: 'Ho già esperienza',
    imageSrc: '/assets/images/onboarding/percorso-si-strumento.svg',
    value: 'Ho già esperienza',
  },
];

const MusicJourneySelection: React.FC<MusicJourneySelectionProps> = ({ eventBus }) => {
  const { skillLevelsOptions, profileSkillLevel, setProfileSkillLevel } = useOnboarding(eventBus);
  const { isLoading } = useOnboardingStore();

  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedLevel, setSelectedLevel] = useState<string>(
    profileSkillLevel?.skillLevel || levelOptions[1].value,
  );
  const [selectedOption, setSelectedOption] = useState<string>('');

  React.useEffect(() => {
    if (
      profileSkillLevel &&
      !levelOptions.map((el) => el.value).includes(profileSkillLevel.skillLevel)
    ) {
      setSelectedOption(profileSkillLevel.skillLevel);
    }
  }, [profileSkillLevel]);

  const handleLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === levelOptions[0].value) {
      eventBus.emit(OnboardingEventKeys.levelSelection.selection.do, {
        skillLevel: event.target.value,
      });
      eventBus.emit(OnboardingEventKeys.levelSelection.selection.view, undefined);
      setSelectedOption('');
    }
    setSelectedLevel(event.target.value);
    setProfileSkillLevel({
      skillLevel: event.target.value,
    });
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    eventBus.emit(OnboardingEventKeys.levelSelection.selection.do, {
      skillLevel: event.target.value,
    });
    setSelectedOption(event.target.value);
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleEnd = () => {
    // Emettiamo l'evento di end onboarding
    eventBus.emit(OnboardingEventKeys.end.do, undefined);
  };

  const selectedFeedback = skillLevelsOptions?.find(
    (option) => option.onboardingDescription === selectedOption,
  )?.onboardingDescriptionAdditional;

  if (!skillLevelsOptions) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Stepper */}
      <Fade
        in={true}
        style={{
          transitionDelay: '50ms',
        }}
      >
        <div>
          <Stepper
            activeStep={2}
            onBack={() => eventBus.emit(OnboardingEventKeys.studySessions.view, undefined)}
          />
        </div>
      </Fade>

      <section className="flex flex-col pt-10 pb-12 gap-y-8 items-center max-w-[650px] m-auto">
        {/* Heading */}
        <Fade
          in={true}
          style={{
            transitionDuration: '700ms',
          }}
        >
          <div>
            <Heading
              title="Quale strumento vuoi suonare?"
              subtitle="Scegli che percorso vuoi intraprendere per il seguente strumento"
            />
          </div>
        </Fade>

        {/* Accordion for instrument level */}
        <Grow in={true} style={{ transitionDelay: '50ms' }}>
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleAccordionChange('panel1')}
            className="w-full"
          >
            <AccordionSummary
              expandIcon={<ExpandMore color="primary" />}
              aria-controls="panel1-content"
              id="panel1-header"
              className="px-6 py-2"
            >
              <Typography variant="s2" color="textPrimary">
                Ho già esperienza
              </Typography>
            </AccordionSummary>
            <AccordionDetails className="px-6 pb-6">
              {/* CreativeRadio buttons for level selection */}
              <div className="grid w-full grid-cols-2 gap-4">
                {levelOptions.map((levelOption) => (
                  <CreativeRadio
                    key={levelOption.value}
                    imageSrc={levelOption.imageSrc}
                    altText={levelOption.name}
                    name={levelOption.name}
                    value={levelOption.value}
                    checked={selectedLevel === levelOption.value}
                    onChange={handleLevelChange}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        </Grow>

        {/* Second Question */}
        <Fade in={true} style={{ transitionDelay: '50ms' }}>
          <Typography variant="h3" component="h2" color="textPrimary" className="text-center">
            Quale frase ti rappresenta di più?
          </Typography>
        </Fade>

        {/* Option Group for journey description */}
        <Grow
          in={true}
          style={{
            transformOrigin: 'center center',
            transitionDelay: '50ms',
          }}
        >
          <div className="space-y-4">
            <RadioOptionGroup
              options={skillLevelsOptions.slice(1).map((option) => option.onboardingDescription)}
              value={selectedOption}
              onChange={handleOptionChange}
              labelPlacement="start"
            />
          </div>
        </Grow>

        {/* Feedback displayed based on selected option */}
        {selectedFeedback && (
          <Grow
            in={Boolean(selectedOption)}
            style={{
              transformOrigin: 'top center',
              transitionDuration: '200ms',
            }}
          >
            <div>
              <OptionSelectedFeedback
                imageSrc="/assets/images/onboarding/libro.svg"
                text={selectedFeedback ?? ''}
                alt="consiglio"
                boxClassName="bg-paper"
              />
            </div>
          </Grow>
        )}

        {/* Continue Button */}
        <Grow in={true}>
          <div>
            <Button
              size="large"
              variant="contained"
              color="primary"
              className="max-w-full w-[220px] gap-2"
              disabled={
                (selectedLevel === levelOptions[1].value && selectedFeedback === '') || isLoading
              }
              onClick={handleEnd}
            >
              {isLoading && <CircularProgress size={16} color="inherit" />}
              <Typography variant="button-l">Continua</Typography>
            </Button>
          </div>
        </Grow>
      </section>
    </>
  );
};

export default MusicJourneySelection;
